import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Friends,
  type FriendListItem,
  type FriendPendingInvite,
  type FriendShareLevel,
  type FriendShareScope,
} from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Icon,
  Input,
  Pill,
  Skeleton,
  Spinner,
} from '@/design-system';
import { useToasts } from '@/lib/store';

const SHARE_SCOPES: { id: FriendShareScope; label: string; description: string }[] = [
  { id: 'cellar', label: 'Kjeller', description: 'Hvilke flasker du har' },
  { id: 'checkins', label: 'Smakinger', description: 'Smaksnotater og hva du drikker' },
  { id: 'wishlist', label: 'Ønskeliste', description: 'Det du har lyst på' },
  { id: 'tastings', label: 'Smaks-historikk', description: 'Vurderinger og notater' },
  { id: 'pantry', label: 'Bar-pantry', description: 'Mixers, sirup, garnish' },
];

const LEVELS: { id: FriendShareLevel; label: string; tone: 'mist' | 'gold' | 'ruby' }[] = [
  { id: 'hidden', label: 'Skjult', tone: 'mist' },
  { id: 'counts', label: 'Antall', tone: 'gold' },
  { id: 'full', label: 'Alt', tone: 'ruby' },
];

export function FriendsPage() {
  const qc = useQueryClient();
  const { push } = useToasts();
  const [email, setEmail] = useState('');
  const [expandedFriendId, setExpandedFriendId] = useState<string | null>(null);

  const friendsQ = useQuery({ queryKey: ['friends'], queryFn: Friends.list });

  const requestM = useMutation({
    mutationFn: (e: string) => Friends.request(e),
    onSuccess: (result) => {
      if (result.kind === 'invite_sent') {
        push({
          title:
            result.delivery === 'email'
              ? 'Invitasjon sendt'
              : 'Invitasjon registrert',
          description:
            result.delivery === 'email'
              ? `Vi sendte en e-post til ${result.email}. Vennskapet kobles automatisk når de logger inn.`
              : `${result.email} er på listen, men e-posttjenesten er ikke konfigurert.`,
          tone: 'success',
        });
      } else {
        const verb = result.link.status === 'accepted' ? 'lagt til' : 'sendt';
        push({ title: `Forespørsel ${verb}`, description: result.link.email, tone: 'success' });
      }
      setEmail('');
      void qc.invalidateQueries({ queryKey: ['friends'] });
    },
    onError: (err: Error) => push({ title: 'Feil', description: err.message, tone: 'danger' }),
  });

  const cancelInviteM = useMutation({
    mutationFn: (e: string) => Friends.cancelInvite(e),
    onSuccess: () => {
      push({ title: 'Invitasjon avbrutt', tone: 'info' });
      void qc.invalidateQueries({ queryKey: ['friends'] });
    },
    onError: (err: Error) => push({ title: 'Feil', description: err.message, tone: 'danger' }),
  });

  const acceptM = useMutation({
    mutationFn: (userId: string) => Friends.accept(userId),
    onSuccess: () => {
      push({ title: 'Venner!', tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['friends'] });
    },
    onError: (err: Error) => push({ title: 'Feil', description: err.message, tone: 'danger' }),
  });

  const removeM = useMutation({
    mutationFn: (userId: string) => Friends.remove(userId),
    onSuccess: () => {
      push({ title: 'Fjernet', tone: 'info' });
      void qc.invalidateQueries({ queryKey: ['friends'] });
      setExpandedFriendId(null);
    },
    onError: (err: Error) => push({ title: 'Feil', description: err.message, tone: 'danger' }),
  });

  const accepted = friendsQ.data?.accepted ?? [];
  const incoming = friendsQ.data?.incoming ?? [];
  const outgoing = friendsQ.data?.outgoing ?? [];
  const pendingInvites = friendsQ.data?.pendingInvites ?? [];

  return (
    <div className="grid gap-6">
      <header className="grid gap-1">
        <h1 className="ui-section-title text-3xl">Venner</h1>
        <p className="text-sm text-mist">
          Del kjelleren, smakinger og ønskeliste — du bestemmer hvor mye hver enkelt får se.
        </p>
      </header>

      <Card className="grid gap-3 p-5">
        <h2 className="font-serif text-xl text-parchment">Legg til venn</h2>
        <p className="text-xs text-mist">
          Vi sender en forespørsel — den andre må godkjenne. Hvis vennen ikke har Alkisen ennå, sender vi en invitasjon på e-post.
        </p>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = email.trim();
            if (trimmed.length === 0) return;
            requestM.mutate(trimmed);
          }}
        >
          <div className="flex-1 min-w-[220px]">
            <Input
              label="E-post"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="venn@example.com"
              required
            />
          </div>
          <Button
            type="submit"
            variant="primary"
            iconLeft="add-bottle"
            loading={requestM.isPending}
            disabled={!email.includes('@')}
          >
            Send forespørsel
          </Button>
        </form>
      </Card>

      {friendsQ.isLoading && (
        <Card className="grid gap-3 p-5">
          <Skeleton height={20} width="40%" /><Skeleton height={60} /><Skeleton height={60} />
        </Card>
      )}

      {incoming.length > 0 && (
        <section className="grid gap-3">
          <h2 className="ui-section-title text-xl">Inviterer deg</h2>
          {incoming.map((f) => (
            <Card key={f.userId} className="grid grid-cols-[1fr_auto] items-center gap-3 p-4">
              <FriendIdentity friend={f} />
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  iconLeft="check"
                  loading={acceptM.isPending}
                  onClick={() => acceptM.mutate(f.userId)}
                >
                  Godta
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft="x"
                  onClick={() => removeM.mutate(f.userId)}
                >
                  Avslå
                </Button>
              </div>
            </Card>
          ))}
        </section>
      )}

      {(outgoing.length > 0 || pendingInvites.length > 0) && (
        <section className="grid gap-3">
          <h2 className="ui-section-title text-xl">Sendt</h2>
          {outgoing.map((f) => (
            <Card key={f.userId} className="grid grid-cols-[1fr_auto] items-center gap-3 p-4">
              <FriendIdentity friend={f} />
              <div className="flex items-center gap-2">
                <Badge tone="mist">venter</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  iconLeft="x"
                  onClick={() => removeM.mutate(f.userId)}
                >
                  Avbryt
                </Button>
              </div>
            </Card>
          ))}
          {pendingInvites.map((invite) => (
            <PendingInviteRow
              key={invite.email}
              invite={invite}
              onCancel={() => cancelInviteM.mutate(invite.email)}
              cancelling={cancelInviteM.isPending}
            />
          ))}
        </section>
      )}

      <section className="grid gap-3">
        <h2 className="ui-section-title text-xl">Vennene dine</h2>
        {!friendsQ.isLoading && accepted.length === 0 ? (
          <Card className="p-6">
            <EmptyState
              icon="users"
              title="Ingen venner enda"
              description="Send en forespørsel over for å begynne å dele kjelleren."
            />
          </Card>
        ) : (
          <div className="grid gap-3">
            {accepted.map((f) => (
              <FriendRow
                key={f.userId}
                friend={f}
                expanded={expandedFriendId === f.userId}
                onToggle={() =>
                  setExpandedFriendId(expandedFriendId === f.userId ? null : f.userId)
                }
                onRemove={() => removeM.mutate(f.userId)}
                removing={removeM.isPending}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function PendingInviteRow({
  invite,
  onCancel,
  cancelling,
}: {
  invite: FriendPendingInvite;
  onCancel: () => void;
  cancelling: boolean;
}) {
  const initial = (invite.email || '?').slice(0, 1).toUpperCase();
  return (
    <Card className="grid grid-cols-[1fr_auto] items-center gap-3 p-4">
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-full bg-candle/10 text-candle/70 font-serif">
          {initial}
        </div>
        <div className="grid">
          <span className="font-serif text-base text-parchment line-clamp-1">{invite.email}</span>
          <span className="text-xs text-mist line-clamp-1">
            Invitert til Alkisen — venter på at de logger inn
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Badge tone="gold">invitert</Badge>
        <Button
          variant="ghost"
          size="sm"
          iconLeft="x"
          onClick={onCancel}
          loading={cancelling}
        >
          Avbryt
        </Button>
      </div>
    </Card>
  );
}

function FriendIdentity({ friend }: { friend: FriendListItem }) {
  const initial = (friend.displayName || friend.email || '?').slice(0, 1).toUpperCase();
  return (
    <div className="flex items-center gap-3">
      <div className="grid h-10 w-10 place-items-center rounded-full bg-candle/15 text-candle font-serif">
        {initial}
      </div>
      <div className="grid">
        <span className="font-serif text-base text-parchment line-clamp-1">
          {friend.displayName || friend.email}
        </span>
        {friend.displayName && <span className="text-xs text-mist line-clamp-1">{friend.email}</span>}
      </div>
    </div>
  );
}

function FriendRow({
  friend,
  expanded,
  onToggle,
  onRemove,
  removing,
}: {
  friend: FriendListItem;
  expanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
  removing: boolean;
}) {
  return (
    <Card className="grid gap-3 p-4">
      <div className="grid grid-cols-[1fr_auto] items-center gap-3">
        <FriendIdentity friend={friend} />
        <div className="flex items-center gap-2">
          <Link to={`/friends/${friend.userId}`} className="ui-btn ui-btn--ghost ui-btn--sm">
            <Icon name="eye" size={14} /> Se baren
          </Link>
          <Button
            variant="ghost"
            size="sm"
            iconLeft={expanded ? 'chevron-up' : 'chevron-down'}
            onClick={onToggle}
          >
            Personvern
          </Button>
        </div>
      </div>
      {expanded && <FriendShareEditor friendUserId={friend.userId} onRemove={onRemove} removing={removing} />}
    </Card>
  );
}

function FriendShareEditor({
  friendUserId,
  onRemove,
  removing,
}: {
  friendUserId: string;
  onRemove: () => void;
  removing: boolean;
}) {
  const qc = useQueryClient();
  const { push } = useToasts();
  const sharesQ = useQuery({
    queryKey: ['friends.shares', friendUserId],
    queryFn: () => Friends.shares(friendUserId),
  });

  const setM = useMutation({
    mutationFn: (input: { scope: FriendShareScope; level: FriendShareLevel }) =>
      Friends.setShares(friendUserId, [input]),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['friends.shares', friendUserId] });
    },
    onError: (err: Error) => push({ title: 'Feil', description: err.message, tone: 'danger' }),
  });

  const outgoing = sharesQ.data?.outgoing;
  const incoming = sharesQ.data?.incoming;

  if (sharesQ.isLoading || !outgoing || !incoming) {
    return (
      <div className="grid gap-2 border-t border-line-soft pt-3">
        <Skeleton height={16} width="40%" />
        <Skeleton height={48} />
      </div>
    );
  }

  return (
    <div className="grid gap-4 border-t border-line-soft pt-4">
      <div className="grid gap-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs uppercase tracking-widest text-mist">Du deler</h4>
          {setM.isPending && <Spinner size={14} />}
        </div>
        <div className="grid gap-2">
          {SHARE_SCOPES.map((scope) => (
            <ScopeRow
              key={scope.id}
              scope={scope}
              current={outgoing[scope.id]}
              onChange={(level) => setM.mutate({ scope: scope.id, level })}
            />
          ))}
        </div>
      </div>

      <div className="grid gap-3">
        <h4 className="text-xs uppercase tracking-widest text-mist">De deler</h4>
        <div className="flex flex-wrap gap-2">
          {SHARE_SCOPES.map((scope) => {
            const lvl = incoming[scope.id];
            return (
              <Badge key={scope.id} tone={lvl === 'full' ? 'ruby' : lvl === 'counts' ? 'gold' : 'mist'}>
                {scope.label}: {labelForLevel(lvl)}
              </Badge>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          iconLeft="trash"
          onClick={onRemove}
          loading={removing}
        >
          Fjern venn
        </Button>
      </div>
    </div>
  );
}

function ScopeRow({
  scope,
  current,
  onChange,
}: {
  scope: { id: FriendShareScope; label: string; description: string };
  current: FriendShareLevel;
  onChange: (level: FriendShareLevel) => void;
}) {
  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-md border border-line-soft p-3">
      <div>
        <div className="font-serif text-sm text-parchment">{scope.label}</div>
        <div className="text-[11px] text-mist">{scope.description}</div>
      </div>
      <div className="flex gap-1">
        {LEVELS.map((l) => (
          <Pill
            key={l.id}
            active={current === l.id}
            variant={l.tone === 'ruby' ? 'ruby' : l.tone === 'gold' ? 'gold' : 'default'}
            onClick={() => onChange(l.id)}
          >
            {l.label}
          </Pill>
        ))}
      </div>
    </div>
  );
}

function labelForLevel(l: FriendShareLevel): string {
  switch (l) {
    case 'full':
      return 'alt';
    case 'counts':
      return 'antall';
    case 'hidden':
    default:
      return 'skjult';
  }
}
